import { useCallback, useEffect, useRef, useState } from 'react';
import { reduced } from '../lib/motion';
import './Work.css';

/* ══════════════════════════════════════════════════════════════
   WORK — scroll-pinned horizontal showcase + archive
   ══════════════════════════════════════════════════════════════ */

const Arrow = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="7" y1="17" x2="17" y2="7" /><polyline points="7 7 17 7 17 17" />
  </svg>
);

const Git = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
  </svg>
);

/* ── Abstract product interfaces (illustrative, not screenshots) ─ */

const MockGallery = () => (
  <div className="mk mk--gallery">
    <div className="mk-toolbar">
      <span className="mk-pill" />
      <span className="mk-pill mk-pill--wide" />
      <span className="mk-cta" />
    </div>
    <div className="mk-tiles">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <span key={i} className={`mk-tile ${i === 1 ? 'is-active' : ''}`} style={{ animationDelay: `${i * 0.28}s` }}>
          <i />
        </span>
      ))}
    </div>
    <div className="mk-progress">
      <span className="mk-progress-label">rendering · 4096px</span>
      <span className="mk-progress-rail"><i /></span>
    </div>
  </div>
);

const MockDocs = () => (
  <div className="mk mk--docs">
    <div className="mk-toolbar">
      <span className="mk-pill" />
      <span className="mk-pill mk-pill--wide" />
      <span className="mk-cta" />
    </div>
    <div className="mk-doc-body">
      <div className="mk-doc-page">
        {[82, 96, 64, 90, 74, 88, 58].map((w, i) => (
          <span key={i} className="mk-line" style={{ width: `${w}%`, animationDelay: `${i * 0.14}s` }} />
        ))}
        <span className="mk-highlight" />
      </div>
      <div className="mk-doc-side">
        {[
          ['due', '14 Mar'],
          ['party', 'ACME SRL'],
          ['amount', '12,400'],
          ['status', 'flagged'],
        ].map(([k, v], i) => (
          <span key={k} className="mk-field" style={{ animationDelay: `${0.3 + i * 0.16}s` }}>
            <i>{k}</i><b>{v}</b>
          </span>
        ))}
      </div>
    </div>
  </div>
);

const MockDash = () => (
  <div className="mk mk--dash">
    <div className="mk-toolbar">
      <span className="mk-pill" />
      <span className="mk-pill mk-pill--wide" />
      <span className="mk-cta" />
    </div>
    <div className="mk-chart">
      <svg viewBox="0 0 300 110" preserveAspectRatio="none">
        <defs>
          <linearGradient id="mkg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(217,255,61,0.32)" />
            <stop offset="100%" stopColor="rgba(217,255,61,0)" />
          </linearGradient>
        </defs>
        <path className="mk-area" d="M0 96 L28 82 L56 88 L84 61 L112 68 L140 44 L168 50 L196 30 L224 36 L252 20 L280 24 L300 14 L300 110 L0 110 Z" fill="url(#mkg)" />
        <path className="mk-spark" d="M0 96 L28 82 L56 88 L84 61 L112 68 L140 44 L168 50 L196 30 L224 36 L252 20 L280 24 L300 14" fill="none" stroke="#d9ff3d" strokeWidth="1.6" />
      </svg>
    </div>
    <div className="mk-rows">
      {[
        ['run · 041', '0.0184', 92],
        ['run · 040', '0.0231', 78],
        ['run · 039', '0.0402', 54],
      ].map(([a, b, w], i) => (
        <span key={a} className="mk-row" style={{ animationDelay: `${0.25 + i * 0.18}s` }}>
          <i>{a}</i>
          <span className="mk-bar"><b style={{ width: `${w}%` }} /></span>
          <em>{b}</em>
        </span>
      ))}
    </div>
  </div>
);

const PANELS = [
  {
    id: 'rovaine',
    n: '01',
    title: 'Rovaine',
    kicker: 'Consumer platform · open source',
    summary:
      'A photo goes in, a finished 4K print-ready portrait comes out. Upload handling, a queued render pipeline with retries, checkout and delivery — built as one system so the customer never sees the machinery.',
    role: 'Architecture · Full-stack · Infrastructure',
    metrics: [['4K', 'output pipeline'], ['Queued', 'render jobs'], ['Stripe', 'checkout']],
    tags: ['React', 'Node.js', 'Stripe', 'Object storage', 'Cloud'],
    live: null,
    repo: 'https://github.com/rarespoanta27/Rovaine',
    mock: <MockGallery />,
  },
  {
    id: 'deadliq',
    n: '02',
    title: 'DeadliQ',
    kicker: 'B2B SaaS · open source',
    summary:
      'Contracts and invoices go in, structured deadlines come out. OCR, a parser that resolves dates, parties and amounts, and a reminder engine that removes the cost of a missed date. Multi-tenant from the first commit.',
    role: 'Product architecture · Backend · Billing',
    metrics: [['OCR', 'Cloud Vision'], ['Multi-tenant', 'from commit 1'], ['Automated', 'reminders']],
    tags: ['Node.js', 'Express', 'MongoDB', 'Google Cloud Vision', 'Stripe', 'React'],
    live: null,
    repo: 'https://github.com/rarespoanta27/DeadliQ',
    mock: <MockDocs />,
  },
  {
    id: 'tuniq',
    n: '03',
    title: 'TuniQ',
    kicker: 'Developer tooling · open source',
    summary:
      'A control plane for model tuning. The React front end drives Python jobs, streams results back over the wire, and puts every run side by side so a non-specialist can pick the winner without touching a terminal.',
    role: 'Full-stack · Job orchestration',
    metrics: [['Live', 'run metrics'], ['Optuna', 'search'], ['OAuth', 'multi-user']],
    tags: ['React', 'Python', 'TensorFlow', 'Optuna', 'OAuth'],
    live: null,
    repo: 'https://github.com/rarespoanta27/TuniQ',
    mock: <MockDash />,
  },
];

const ARCHIVE = [
  { t: 'EasyCars', d: 'Car rental platform — browsing, cart, orders, JWT auth.', s: ['React', 'Node.js', 'MySQL', 'JWT'], u: 'https://github.com/rarespoanta27/EasyCars' },
  { t: 'Invoice Generator', d: 'Secure invoicing with dynamic PDF generation and automated delivery.', s: ['JavaScript', 'PDF', 'Auth'], u: 'https://github.com/rarespoanta27/Invoice-Generator' },
  { t: 'LEMP Installer', d: 'Bash automation for production-ready servers with security hardening.', s: ['Bash', 'Nginx', 'MariaDB', 'Linux'], u: 'https://github.com/rarespoanta27/lemp-installer' },
  { t: 'System Monitor', d: 'Real-time colour-coded CLI overview of Linux resources and network.', s: ['Bash', 'CLI', 'Monitoring'], u: 'https://github.com/rarespoanta27/system-monitor-script' },
  { t: 'ClotheStore', d: 'Full-stack e-commerce with search, cart and admin management.', s: ['Full-Stack', 'E-commerce'], u: 'https://github.com/rarespoanta27/ClotheStore' },
  { t: 'Inventory Manager', d: 'Typed inventory system with an in-memory store and modern UI.', s: ['TypeScript', 'Vite'], u: 'https://github.com/rarespoanta27/Simple-Inventory-Management-System' },
  { t: 'X Sentiment Analysis', d: 'RPA pipeline classifying posts into positive, neutral and negative.', s: ['UiPath', 'RPA'], u: 'https://github.com/rarespoanta27/X-Sentiment-Analysis' },
  { t: 'Projectile Simulator', d: 'Android app simulating trajectories from physics parameters.', s: ['Java', 'Android'], u: 'https://github.com/rarespoanta27/ProjectileSimulator' },
  { t: 'Rock Paper Scissors', d: 'Playful React build with themes, confetti and a custom cursor.', s: ['React', 'CSS'], u: 'https://github.com/rarespoanta27/Rock-paper-scissors-game' },
];

/* ── One showcase panel ───────────────────────────────────────── */
function Panel({ p }) {
  const tiltRef = useRef(null);

  const onMove = useCallback((e) => {
    const el = tiltRef.current;
    if (!el || reduced()) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    el.style.transform = `perspective(1400px) rotateY(${px * 11}deg) rotateX(${-py * 9}deg)`;
  }, []);

  const onLeave = useCallback(() => {
    const el = tiltRef.current;
    if (el) el.style.transform = '';
  }, []);

  return (
    <article className="sc-panel">
      <div className="sc-copy">
        <span className="sc-n" aria-hidden="true">{p.n}</span>

        <div className="sc-copy-head">
          <span className="mono sc-kicker">{p.kicker}</span>
          {p.live && <span className="avail avail--sm"><i className="avail-led" />Live</span>}
        </div>

        <h3 className="sc-title">{p.title}</h3>
        <p className="sc-summary">{p.summary}</p>

        <div className="sc-metrics">
          {p.metrics.map(([v, l]) => (
            <div className="sc-metric" key={l}>
              <span className="sc-metric-v">{v}</span>
              <span className="sc-metric-l">{l}</span>
            </div>
          ))}
        </div>

        <div className="sc-role">
          <span className="mono">Role</span>
          <span>{p.role}</span>
        </div>

        <div className="sc-tags">
          {p.tags.map((t) => <span className="tag" key={t}>{t}</span>)}
        </div>

        <div className="sc-actions">
          {p.live && (
            <a href={p.live} target="_blank" rel="noopener noreferrer" className="btn btn--volt">
              Visit live site
              <span className="btn-ico"><Arrow /></span>
            </a>
          )}
          {p.repo && (
            <a href={p.repo} target="_blank" rel="noopener noreferrer" className={`btn ${p.live ? '' : 'btn--volt'}`}>
              Source code
              <span className="btn-ico"><Git /></span>
            </a>
          )}
        </div>
      </div>

      <div className="sc-visual" onPointerMove={onMove} onPointerLeave={onLeave}>
        <span className="sc-visual-glow" aria-hidden="true" />
        <div className="sc-frame" ref={tiltRef}>
          <div className="sc-frame-bar">
            <span className="sc-frame-dots"><i /><i /><i /></span>
            <span className="sc-frame-url">
              {(p.live || p.repo || '').replace('https://', '')}
            </span>
          </div>
          <div className="sc-frame-body">{p.mock}</div>
        </div>
      </div>
    </article>
  );
}

export default function Work() {
  const sectionRef = useRef(null);
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(false);
  const shown = open ? ARCHIVE : ARCHIVE.slice(0, 4);

  /* Pin + horizontal drive */
  useEffect(() => {
    const section = sectionRef.current;
    const track = trackRef.current;
    if (!section || !track) return undefined;

    const mq = window.matchMedia('(min-width: 1000px)');
    let ticking = false;

    const update = () => {
      ticking = false;
      if (!mq.matches || reduced()) {
        track.style.transform = '';
        track.style.removeProperty('--panel-w');
        return;
      }
      track.style.setProperty('--panel-w', `${track.parentElement.clientWidth}px`);
      const rect = section.getBoundingClientRect();
      const distance = section.offsetHeight - window.innerHeight;
      const p = distance > 0 ? Math.max(0, Math.min(1, -rect.top / distance)) : 0;
      const span = Math.max(0, track.scrollWidth - track.parentElement.clientWidth);
      track.style.transform = `translate3d(${-p * span}px, 0, 0)`;
      setActive(Math.min(PANELS.length - 1, Math.round(p * (PANELS.length - 1))));
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  return (
    <>
      {/* ── Pinned showcase ─────────────────────────────── */}
      <section id="work" className="sc" ref={sectionRef}>
        <div className="sc-sticky">
          <div className="sc-head shell">
            <div className="sc-head-copy">
              <span className="sec-tag"><b>01</b> — Selected work</span>
              <h2 className="sc-headline display-md">
                <span className="mask-line"><span>Built end to end. Code in the open.</span></span>
              </h2>
            </div>
            <div className="sc-counter" aria-hidden="true">
              <span className="sc-counter-now">{String(active + 1).padStart(2, '0')}</span>
              <span className="sc-counter-sep">/</span>
              <span className="sc-counter-all">{String(PANELS.length).padStart(2, '0')}</span>
            </div>
          </div>

          <div className="sc-viewport">
            <div className="sc-track" ref={trackRef}>
              {PANELS.map((p) => <Panel p={p} key={p.id} />)}
            </div>
          </div>

          <div className="sc-rail shell">
            <span className="mono sc-rail-hint">Keep scrolling</span>
            <span className="sc-rail-bars" aria-hidden="true">
              {PANELS.map((p, i) => <i key={p.id} className={i <= active ? 'on' : ''} />)}
            </span>
            <span className="mono sc-rail-name">{PANELS[active].title}</span>
          </div>
        </div>
      </section>

      {/* ── Archive ─────────────────────────────────────── */}
      <section className="band band--tight">
        <div className="shell">
          <div className="wk-archive-head" data-rv="up">
            <span className="mono">Archive — {ARCHIVE.length} more builds</span>
            <span className="wk-archive-rule" />
            <a href="https://github.com/rarespoanta27" target="_blank" rel="noopener noreferrer" className="wk-archive-link">
              All repositories<span className="btn-ico"><Arrow /></span>
            </a>
          </div>

          <ul className="wk-list">
            {shown.map((a, i) => (
              <li key={a.t} className="wk-item" data-rv="up" style={{ '--rv-d': `${i * 60}ms` }}>
                <a href={a.u} target="_blank" rel="noopener noreferrer" className="wk-item-link" data-cur="GITHUB">
                  <span className="wk-item-idx">{String(i + 4).padStart(2, '0')}</span>
                  <span className="wk-item-body">
                    <span className="wk-item-title">{a.t}</span>
                    <span className="wk-item-desc">{a.d}</span>
                  </span>
                  <span className="wk-item-tags">
                    {a.s.map((s) => <span className="tag" key={s}>{s}</span>)}
                  </span>
                  <span className="wk-item-arrow"><Arrow /></span>
                </a>
              </li>
            ))}
          </ul>

          <button className="wk-more" onClick={() => setOpen((o) => !o)}>
            <span>{open ? 'Collapse archive' : `Show ${ARCHIVE.length - 4} more`}</span>
            <svg className={`wk-more-ico ${open ? 'is-open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        </div>
      </section>
    </>
  );
}
